// GuestAccess - viewer guest access page
// Implements US-1.2: Viewer Guest Access

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { loginViewer } from '../services/authService';
import { storeViewerToken } from '../utils/tokenStorage';
import styles from './GuestAccess.module.css';

/**
 * RENDER guest access page with viewer authentication
 * Allows joining workspace with room code and display name
 */
const GuestAccess: React.FC = () => {
  const navigate = useNavigate();
  const [roomCode, setRoomCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * HANDLE form submission
   * Validates inputs and authenticates viewer
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // VALIDATE inputs
    const trimmedCode = roomCode.trim();
    const trimmedName = displayName.trim();

    if (!trimmedCode) {
      setError('Bitte gib einen Raum Code ein');
      return;
    }

    if (!trimmedName) {
      setError('Bitte gib deinen Anzeigenamen ein');
      return;
    }

    if (trimmedName.length > 50) {
      setError('Anzeigename darf maximal 50 Zeichen lang sein');
      return;
    }

    // AUTHENTICATE viewer
    setIsLoading(true);
    try {
      const response = await loginViewer(trimmedCode, trimmedName);

      if (response.success && response.token && response.workspace) {
        // STORE viewer token
        storeViewerToken(response.token);

        // NAVIGATE to workspace
        navigate(`/workspace/${response.workspace.id}`);
      } else {
        setError('Workspace konnte nicht beigetreten werden. Bitte versuche es erneut.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Workspace konnte nicht beigetreten werden');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className={styles.content}>
        {/* LOGO AND BRANDING */}
        <div className={styles.branding}>
          <img
            src={require('../assets/insurtale-logo.webp')}
            alt="insur_tale Logo"
            className={styles.logo}
          />
          <h1 className={styles.appTitle}>Arbiter</h1>
        </div>

        {/* PAGE TITLE */}
        <h2 className={styles.pageTitle}>Als Gast beitreten</h2>
        <p className={styles.subtitle}>
          Tritt einem Workspace mit einem Raum Code bei, um ohne Account mitzuarbeiten
        </p>

        {/* ERROR display */}
        {error && (
          <div className={styles.errorNotice}>
            <strong>Fehler:</strong> {error}
          </div>
        )}

        {/* GUEST ACCESS form */}
        <form className={styles.form} onSubmit={handleSubmit}>
          <div className={styles.formGroup}>
            <label htmlFor="roomCode" className={styles.label}>
              Raum Code
            </label>
            <input
              id="roomCode"
              type="text"
              className={`${styles.input} ${styles.roomCode}`}
              value={roomCode}
              onChange={(e) => setRoomCode(e.target.value.toUpperCase())}
              placeholder="ABCD1234"
              maxLength={8}
              disabled={isLoading}
              required
            />
            <span className={styles.helpText}>
              Gib den 8-stelligen Raum Code ein, den du von der Workspace-Ersteller:in erhalten hast
            </span>
          </div>

          <div className={styles.formGroup}>
            <label htmlFor="displayName" className={styles.label}>
              Dein Anzeigename
            </label>
            <input
              id="displayName"
              type="text"
              className={styles.input}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="John Doe"
              maxLength={50}
              disabled={isLoading}
              required
            />
            <span className={styles.helpText}>
              Dieser Name wird für andere Teilnehmer:innen sichtbar sein (max. 50 Zeichen)
            </span>
          </div>

          <button type="submit" className={styles.button} disabled={isLoading}>
            {isLoading ? 'Trete bei...' : 'Workspace beitreten'}
          </button>
        </form>

        {/* INFO box about guest access */}
        <div className={styles.infoBox}>
          <h3>Als Gast kannst du:</h3>
          <ul>
            <li>Themen / Elemente und Bewertungen in Echtzeit ansehen</li>
            <li>Visualisierungen und Ergebnisse sehen</li>
            <li>Notizen hinzufügen und an Diskussionen teilnehmen</li>
            <li>Sehen, wer sonst noch im Workspace ist</li>
          </ul>
        </div>

        {/* LINK to login */}
{/*         <div className={styles.link}>
          Hast du einen Account? <Link to="/login">Anmelden</Link>
        </div> */}
      </div>
    </Layout>
  );
};

export default GuestAccess;
